import { makeAutoObservable, observable, runInAction } from "mobx";
import { Company } from "../models/Company";
import { agent } from "../api/agent";
import { toast } from "react-toastify";
import i18n from "../../i18n";

export default class CompanyStore {
    companyRegistry = new Map<string, Company>();
    selectedCompany: Company | undefined = undefined;
    editMode = false;
    loading = false;
    loadingInitial = false;
    predicate = new Map();

    constructor() {
        makeAutoObservable(this);
    }

    get companies() {
        return Array.from(this.companyRegistry.values());
    }

    loadUserCompanies = async (UserID: string) => {
        this.setLoadingInitial(true);
        try {
            const companies = await agent.Companies.listUserCompanies(UserID);
            runInAction(() => {
                this.companyRegistry.clear();
                companies.forEach(company => {
                    this.setCompany(company);
                });
                this.setLoadingInitial(false);
            });
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.setLoadingInitial(false);
            });
            toast.error((i18n as any).t('company.loadCompaniesError'));
        }
    };

    loadCompany = async (id: string) => {
        let company = this.getCompany(id);
        if (company) {
            this.selectedCompany = company;
            return company;
        } else {
            this.loadingInitial = true;
            try {
                company = await agent.Companies.details(id);
                runInAction(() => {
                    this.setCompany(company!);
                    this.selectedCompany = company;
                    this.loadingInitial = false;
                });
                return company;
            } catch (error) {
                console.log(error);
                runInAction(() => {
                    this.loadingInitial = false;
                });
                toast.error((i18n as any).t('company.loadCompanyDetailsError'));
            }
        }
    };

    createCompany = async (company: Company) => {
        this.loading = true;
        try {
            // API returns the created company with server-assigned ID
            company.id = '00000000-0000-0000-0000-000000000000';
            const createdCompany = await agent.Companies.create(company);
                     
            runInAction(() => {
                this.companyRegistry.set(createdCompany.id , createdCompany);
                this.selectedCompany = createdCompany;
                this.editMode = false;
                this.loading = false;
            });
            toast.success((i18n as any).t('company.createSuccess'));
            return createdCompany;
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.loading = false;
            });
            toast.error((i18n as any).t('company.createError'));
            return undefined;
        }
    };

    updateCompany = async (company: Company) => {
        this.loading = true;
        try {
            // API directly returns the updated company
            const updatedCompany = await agent.Companies.update(company);
            runInAction(() => {
                updatedCompany.country = company.country;
                this.companyRegistry.set(updatedCompany.id, observable(updatedCompany));
                this.selectedCompany = updatedCompany;
                this.editMode = false;
                this.loading = false;
            });
            toast.success((i18n as any).t('company.updateSuccess'));
            return updatedCompany;
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.loading = false;
            });
            toast.error((i18n as any).t('company.updateError'));
            return undefined;
        }
    };

    deleteCompany = async (id: string) => {
        this.loading = true;
        try {
            await agent.Companies.delete(id);
            runInAction(() => {
                this.companyRegistry.delete(id);
                this.loading = false;
            });
            toast.success((i18n as any).t('company.deleteSuccess'));
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.loading = false;
            });
            toast.error((i18n as any).t('company.deleteError'));
        }
    };

    setLoadingInitial = (state: boolean) => {
        this.loadingInitial = state;
    };

    private getCompany = (id: string) => {
        return this.companyRegistry.get(id);
    };

    private setCompany = (company: Company) => {
        this.companyRegistry.set(company.id, company);
    };

    get axiosParams() {
        const params = new URLSearchParams();

        this.predicate.forEach((value, key) => {
            params.append(key, value.toString());
        });

        return params;
    };
}